// LCP — My settings: what the signed-in user sets for themselves.
//
// Product owner, 2026-11-01 (Jam 9b489c14), signing off inspection
// 2026-000771: "I don't see a way to access my user to change my time zone so
// that the time is accurate on the reports that I sign." Opened from the user
// block at the foot of the LCP sidebar (click your name), so it is there for
// everyone, inspectors included — LCP Settings is for org admins only.
//
// One setting so far: the time zone every report and audit-package PDF you
// generate prints its times in (lcp/utils/reportTime). Saving writes your own
// profile (POST /timezones/user/add) and the in-app user, so the next document
// uses it without signing in again.
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import toast from "react-hot-toast";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { cc } from "afms/theme/commandCenterTheme";
import { TimezonesApiService } from "api/timezones.api";
import { userTimezoneUpdated } from "redux-store/auth/reducers";
import { selectUserDetails } from "config/appMode";
import {
  UNSET_USER_ZONE,
  browserTimeZone,
  formatReportDateTime,
  isUnsetUserZone,
  isValidTimeZone,
  reportZoneName,
} from "lcp/utils/reportTime";

// SoftUI forces width:max-content on .MuiInputBase-input, which makes a
// fullWidth TextField render narrow. Same override the rest of the app uses.
const FULL_INPUT_SX = { "& .MuiInputBase-input": { width: "100% !important" } };

const unwrap = (resp) => resp?.data?.data ?? resp?.data ?? null;
const errMessage = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

// The server's rows, minus the not-chosen-yet placeholder: picking it would
// read back as "not set" (see reportTime.UNSET_USER_ZONE).
export const timezoneOptions = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .filter((r) => r?.timezone && r.timezone !== UNSET_USER_ZONE && isValidTimeZone(r.timezone))
    .map((r) => ({ value: r.timezone, label: r.name || r.timezone }));

const optionFor = (options, zone) =>
  !zone || isUnsetUserZone(zone)
    ? null
    : options.find((o) => o.value === zone) || { value: zone, label: zone };

export default function MySettingsDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const userDetails = selectUserDetails(authUser);
  const current = authUser?.timezone || userDetails?.timezone || null;

  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // The list once per mount; it is the same for everybody and does not change.
  useEffect(() => {
    if (!open || rows) return undefined;
    let cancelled = false;
    setLoadError(null);
    TimezonesApiService.list()
      .then((resp) => {
        if (!cancelled) setRows(unwrap(resp) || []);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errMessage(e, "Could not load the list of time zones."));
      });
    return () => {
      cancelled = true;
    };
  }, [open, rows]);

  const options = useMemo(() => {
    const list = timezoneOptions(rows);
    // A zone the profile holds that the list lacks still shows as chosen.
    if (current && !isUnsetUserZone(current) && !list.some((o) => o.value === current)) {
      list.unshift({ value: current, label: current });
    }
    return list;
  }, [rows, current]);

  // Pre-select the stored zone every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSelected(optionFor(options, current));
    setSaveError(null);
  }, [open, options, current]);

  const device = browserTimeZone();
  const deviceOption = device ? options.find((o) => o.value === device) : null;
  const unchanged = (selected?.value || null) === (isUnsetUserZone(current) ? null : current);
  const previewZone = selected?.value || device || "UTC";

  const handleSave = async () => {
    if (!selected?.value) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await TimezonesApiService.saveMine(selected.value);
      const saved = unwrap(resp)?.timezone || selected.value;
      dispatch(userTimezoneUpdated(saved));
      toast.success(`Time zone saved. Your reports now print in ${reportZoneName(saved)}.`);
      onClose?.();
    } catch (e) {
      setSaveError(errMessage(e, "Could not save your time zone."));
    } finally {
      setSaving(false);
    }
  };

  const displayName =
    [userDetails?.firstName, userDetails?.lastName].filter(Boolean).join(" ") ||
    userDetails?.email ||
    "";

  return (
    <Dialog open={!!open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <SoftTypography variant="h6" fontWeight="bold" sx={{ color: cc.ink }}>
          My settings
        </SoftTypography>
        {displayName ? (
          <SoftTypography variant="caption" color="text">
            {displayName}
          </SoftTypography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          <Box>
            <SoftTypography
              component="label"
              htmlFor="lcp-my-time-zone"
              variant="caption"
              fontWeight="bold"
              sx={{ display: "block", mb: 0.5, color: cc.ink }}
            >
              Time zone
            </SoftTypography>
            <Autocomplete
              id="lcp-my-time-zone"
              options={options}
              value={selected}
              loading={!rows && !loadError}
              disabled={saving}
              onChange={(_e, opt) => setSelected(opt || null)}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              getOptionLabel={(o) => o?.label || ""}
              noOptionsText="No matching time zone"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search, e.g. New_York or Chicago"
                  sx={FULL_INPUT_SX}
                />
              )}
            />
          </Box>
          {deviceOption && deviceOption.value !== selected?.value ? (
            <Box>
              <Button
                size="small"
                onClick={() => setSelected(deviceOption)}
                disabled={saving}
                sx={{ textTransform: "none", px: 0, minWidth: 0 }}
              >
                Use this device&apos;s time zone ({deviceOption.value})
              </Button>
            </Box>
          ) : null}
          <SoftTypography variant="caption" color="text" sx={{ display: "block" }}>
            {isUnsetUserZone(current) && !selected
              ? `No time zone chosen yet. Until you choose one, your reports use this device's zone${
                  device ? ` (${device})` : ""
                }.`
              : "Reports and audit documents you generate and sign print their times in this zone."}{" "}
            Right now that reads: <b>{formatReportDateTime(new Date(), previewZone)}</b>
          </SoftTypography>
          {loadError || saveError ? (
            <SoftTypography variant="caption" color="error" sx={{ display: "block" }}>
              {saveError || loadError}
            </SoftTypography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <SoftButton
          variant="gradient"
          color="info"
          onClick={handleSave}
          disabled={saving || !selected?.value || unchanged}
        >
          {saving ? "Saving…" : "Save"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

MySettingsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
